import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Download, Target, Trash2 } from 'lucide-react';
import { formatarPct, formatarReais } from '@profitlens/calculo';
import { Cabecalho } from '../componentes/Cabecalho';
import { Carregando, Erro, Vazio } from '../componentes/Estados';
import { SeloClassificacao } from '../componentes/Classificacao';
import { useAuth } from '../contextos/AuthContext';
import { useProdutos } from '../servicos/produtos';
import { useFornecedores } from '../servicos/fornecedores';
import { useUsuarios } from '../servicos/usuarios';
import {
  atualizarOportunidade,
  COR_STATUS,
  removerOportunidade,
  resumoDasVivas,
  ROTULO_STATUS,
  useOportunidades,
} from '../servicos/oportunidades';
import type { StatusOportunidade } from '../tipos';

/**
 * O que sobrou depois da análise, com dono e decisão.
 *
 * Uma análise favorável solta não vale nada: sem responsável e sem prazo ela
 * vira uma aba aberta que ninguém fecha. Esta tela existe para transformar
 * número em ação — e para tornar visível o que está parado.
 */
const STATUS: StatusOportunidade[] = [
  'ABERTA',
  'EM_NEGOCIACAO',
  'COMPRADA',
  'DESCARTADA',
  'ARQUIVADA',
];

export function Oportunidades() {
  const { oportunidades, carregando, erro } = useOportunidades();
  const { produtos } = useProdutos();
  const { fornecedores } = useFornecedores();
  const { usuarios } = useUsuarios();
  const { perfil, podeEscrever } = useAuth();
  const [parametros, setParametros] = useSearchParams();

  const filtro = parametros.get('status') ?? 'VIVAS';

  const nomeProduto = useMemo(
    () => Object.fromEntries(produtos.map((p) => [p.id, p.nome])) as Record<string, string>,
    [produtos],
  );
  const nomeFornecedor = useMemo(
    () =>
      Object.fromEntries(
        fornecedores.map((f) => [f.id, f.nomeFantasia || f.razaoSocial]),
      ) as Record<string, string>,
    [fornecedores],
  );

  const filtradas = useMemo(() => {
    if (filtro === 'TODAS') return oportunidades;
    if (filtro === 'VIVAS') {
      return oportunidades.filter((o) => o.status === 'ABERTA' || o.status === 'EM_NEGOCIACAO');
    }
    return oportunidades.filter((o) => o.status === filtro);
  }, [oportunidades, filtro]);

  const resumo = resumoDasVivas(oportunidades);

  if (carregando) return <Carregando />;
  if (erro) return <Erro titulo="Não foi possível carregar as oportunidades" detalhe={erro} />;

  function trocarFiltro(v: string) {
    const p = new URLSearchParams(parametros);
    if (v === 'VIVAS') p.delete('status');
    else p.set('status', v);
    setParametros(p, { replace: true });
  }

  return (
    <div className="flex flex-col gap-5">
      <Cabecalho
        titulo="Oportunidades"
        descricao="As análises que viraram decisão. Com dono, para não virarem aba aberta."
        acoes={
          filtradas.length > 0 && (
            <button
              type="button"
              onClick={() => exportarCsv(filtradas, nomeProduto, nomeFornecedor)}
              className="inline-flex items-center gap-1.5 rounded border border-regua-2 px-3 py-1.5 text-sm hover:border-marca hover:text-marca"
            >
              <Download className="size-4" aria-hidden />
              Exportar
            </button>
          )
        }
      />

      {oportunidades.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-4">
          <Cartao rotulo="Em decisão" valor={String(resumo.quantas)} nota={`${resumo.emNegociacao} em negociação`} />
          <Cartao rotulo="Capital comprometido" valor={formatarReais(resumo.capitalCents)} nota="somando as vivas" />
          <Cartao rotulo="Lucro esperado" valor={formatarReais(resumo.lucroCents)} nota="se todas fecharem" />
          <Cartao
            rotulo="Sem responsável"
            valor={String(resumo.semResponsavel)}
            nota={resumo.semResponsavel > 0 ? 'ninguém está tocando' : 'todas com dono'}
            alerta={resumo.semResponsavel > 0}
          />
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <select
          value={filtro}
          onChange={(e) => trocarFiltro(e.target.value)}
          aria-label="Filtrar por situação"
          className="rounded border border-regua-2 bg-superficie px-3 py-1.5 text-sm outline-none focus:border-marca"
        >
          <option value="VIVAS">Em decisão ({resumo.quantas})</option>
          <option value="TODAS">Todas ({oportunidades.length})</option>
          {STATUS.map((s) => (
            <option key={s} value={s}>
              {ROTULO_STATUS[s]} ({oportunidades.filter((o) => o.status === s).length})
            </option>
          ))}
        </select>
      </div>

      {filtradas.length === 0 ? (
        <Vazio
          icone={<Target className="size-8" />}
          titulo={
            oportunidades.length === 0 ? 'Nenhuma oportunidade ainda' : 'Nada neste filtro'
          }
          descricao={
            oportunidades.length === 0
              ? 'Analise um produto e, se a conta fechar, guarde a análise aqui. É o que transforma um número numa decisão com dono.'
              : 'Troque a situação para ver as outras.'
          }
          acao={
            oportunidades.length === 0 && (
              <Link
                to="/viabilidade"
                className="rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90"
              >
                Abrir a análise
              </Link>
            )
          }
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {filtradas.map((o) => (
            <li key={o.id} className="rounded-md border border-regua-2 bg-superficie">
              <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                <Link to={`/mercadorias/${o.produtoId}`} className="font-medium hover:text-marca">
                  {nomeProduto[o.produtoId] ?? 'produto removido'}
                </Link>
                {o.fornecedorId && (
                  <span className="text-xs text-tinta-2">
                    de {nomeFornecedor[o.fornecedorId] ?? 'fornecedor removido'}
                  </span>
                )}
                <SeloClassificacao valor={o.classificacao} />
                <span
                  className={`rounded px-1.5 py-0.5 font-mono text-[0.62rem] font-semibold uppercase ${COR_STATUS[o.status]}`}
                >
                  {ROTULO_STATUS[o.status]}
                </span>

                <span className="ml-auto flex items-center gap-3 text-sm">
                  <span title="Lucro total esperado">{formatarReais(o.lucroTotalCents)}</span>
                  <span className="text-tinta-3" title="Retorno sobre o capital">
                    {formatarPct(o.roiBps)}
                  </span>
                  {podeEscrever && (
                    <button
                      type="button"
                      onClick={() => removerOportunidade(o.id)}
                      aria-label="Remover"
                      className="rounded border border-regua-2 px-2 py-1 text-tinta-3 hover:border-pend hover:text-pend"
                    >
                      <Trash2 className="size-3.5" aria-hidden />
                    </button>
                  )}
                </span>
              </div>

              <div className="flex flex-wrap items-end gap-3 border-t border-regua px-4 py-3">
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium">Situação</span>
                  <select
                    value={o.status}
                    disabled={!podeEscrever}
                    onChange={(e) =>
                      atualizarOportunidade(
                        o.id,
                        { status: e.target.value as StatusOportunidade },
                        perfil?.id ?? '',
                      )
                    }
                    className="rounded border border-regua-2 bg-papel px-2 py-1 text-sm outline-none focus:border-marca disabled:opacity-60"
                  >
                    {STATUS.map((s) => (
                      <option key={s} value={s}>
                        {ROTULO_STATUS[s]}
                      </option>
                    ))}
                  </select>
                </label>

                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium">Responsável</span>
                  <select
                    value={o.responsavelId ?? ''}
                    disabled={!podeEscrever}
                    onChange={(e) =>
                      atualizarOportunidade(
                        o.id,
                        { responsavelId: e.target.value || null },
                        perfil?.id ?? '',
                      )
                    }
                    className="rounded border border-regua-2 bg-papel px-2 py-1 text-sm outline-none focus:border-marca disabled:opacity-60"
                  >
                    <option value="">Ninguém</option>
                    {usuarios
                      .filter((u) => u.ativo)
                      .map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.nome}
                        </option>
                      ))}
                  </select>
                </label>

                <label className="flex min-w-56 flex-1 flex-col gap-1">
                  <span className="text-xs font-medium">Decisão</span>
                  <input
                    type="text"
                    defaultValue={o.decisao ?? ''}
                    disabled={!podeEscrever}
                    placeholder="Por que sim, por que não, o que falta decidir"
                    onBlur={(e) =>
                      e.target.value !== (o.decisao ?? '') &&
                      atualizarOportunidade(
                        o.id,
                        { decisao: e.target.value || null },
                        perfil?.id ?? '',
                      )
                    }
                    className="rounded border border-regua-2 bg-papel px-2 py-1 text-sm outline-none focus:border-marca disabled:opacity-60"
                  />
                </label>
              </div>

              {/*
                Os números do dia da decisão. Refazer a conta hoje daria outro
                resultado — preço de fornecedor muda, tarifa muda, a tabela de
                frete é reajustada — e a decisão passada pareceria errada quando
                foi tomada com outros números.
              */}
              {o.retrato && (
                <p className="border-t border-regua px-4 py-2 text-[0.7rem] text-tinta-3">
                  Quando foi decidida: comprar{' '}
                  <strong className="text-tinta-2">
                    {(o.retrato.quantidadeMilli / 1000).toLocaleString('pt-BR')} un
                  </strong>{' '}
                  a {formatarReais(o.retrato.precoFornecedorCents)} e vender a{' '}
                  {formatarReais(o.retrato.precoVendaCents)} · margem{' '}
                  {formatarPct(o.retrato.margemBps)} · capital{' '}
                  {formatarReais(o.retrato.capitalNecessarioCents)} ·{' '}
                  {o.retrato.anunciosComparaveis} anúncio(s) de mercado
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Cartao({
  rotulo,
  valor,
  nota,
  alerta,
}: {
  rotulo: string;
  valor: string;
  nota: string;
  alerta?: boolean;
}) {
  return (
    <div className="rounded-md border border-regua-2 bg-superficie p-4">
      <p className="text-xs text-tinta-3">{rotulo}</p>
      <p className={`text-2xl font-semibold ${alerta ? 'text-c-res' : ''}`}>{valor}</p>
      <p className="mt-0.5 text-[0.65rem] text-tinta-3">{nota}</p>
    </div>
  );
}

/**
 * Exporta o que está na tela, não a coleção inteira: o filtro faz parte da
 * pergunta que a pessoa está respondendo.
 *
 * Ponto e vírgula como separador, e vírgula decimal — é o que o Excel em
 * português abre sem perguntar nada. Vírgula como separador partiria cada valor
 * monetário em duas colunas.
 */
function exportarCsv(
  linhas: ReturnType<typeof useOportunidades>['oportunidades'],
  nomeProduto: Record<string, string>,
  nomeFornecedor: Record<string, string>,
) {
  const reais = (c: number) => (c / 100).toFixed(2).replace('.', ',');
  const pct = (bps: number) => (bps / 100).toFixed(2).replace('.', ',');
  const limpo = (t: string | null | undefined) => `"${(t ?? '').replace(/"/g, '""')}"`;

  const cabecalho = [
    'Produto', 'Fornecedor', 'Situação', 'Classificação', 'Lucro total', 'ROI %',
    'Quantidade', 'Preço de compra', 'Preço de venda', 'Margem %', 'Capital', 'Decisão',
  ];

  const corpo = linhas.map((o) =>
    [
      limpo(nomeProduto[o.produtoId]),
      limpo(o.fornecedorId ? nomeFornecedor[o.fornecedorId] : ''),
      limpo(ROTULO_STATUS[o.status]),
      limpo(o.classificacao),
      reais(o.lucroTotalCents),
      pct(o.roiBps),
      o.retrato ? (o.retrato.quantidadeMilli / 1000).toString().replace('.', ',') : '',
      o.retrato ? reais(o.retrato.precoFornecedorCents) : '',
      o.retrato ? reais(o.retrato.precoVendaCents) : '',
      o.retrato ? pct(o.retrato.margemBps) : '',
      o.retrato ? reais(o.retrato.capitalNecessarioCents) : '',
      limpo(o.decisao),
    ].join(';'),
  );

  // BOM na frente: sem ele o Excel abre acentuação quebrada.
  const csv = `﻿${cabecalho.join(';')}\n${corpo.join('\n')}`;
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = `oportunidades-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
